import { Dispatch, SetStateAction } from "react";

import { ProjectIssuanceConstants } from "../../../../../constants";
import {
  ProjectIssuanceBNGPanelData,
  ProjectIssuanceCarbonPanelData,
  ProjectIssuanceNNPanelData,
  ProjectIssuanceSpecificProperty,
  ProjectIssuanceSpecificSchema,
  ProjectIssuanceSpecificSchemaCondition,
} from "../../../../../models";
/*
  The leaf module rather than the utils barrel: that barrel reaches Toast, and so the whole widget
  tree and the ESM-only identity service, which aborts a targeted test run of anything importing it.
*/
import { camelCaseToSentenceCase } from "../../../../../utils/string/stringCase";

export type IssuancePanelData =
  | ProjectIssuanceBNGPanelData
  | ProjectIssuanceCarbonPanelData
  | ProjectIssuanceNNPanelData;
export type StandardSpecificIssuanceData = IssuancePanelData["standardSpecificIssuanceData"];

const COMMON_ISSUANCE_FIELD_KEYS = new Set([
  "uuid",
  "status",
  "issuanceDate",
  "quantityType",
  "soldQuantity",
  "reservedQuantity",
  "availableQuantity",
  "quotedQuantity",
  "serialNumber",
  "price",
  "priceCurrency",
  "rowVersion",
  "verifierUuid",
  "totalQuantity",
]);

export const isReadOnlyOrConstField = (definition: ProjectIssuanceSpecificProperty): boolean => {
  return definition.readOnly === true || definition.const !== undefined;
};

/** The server's code for a quantity type the standard does not permit (BR-02914 / BR-02915). */
export const QUANTITY_TYPE_INVALID_ERROR_CODE = "VALIDATION_QUANTITY_TYPE_INVALID";

/**
 * What the quantity-type column is called for a measurement: "Nutrient" on Nutrient Neutrality,
 * "Unit type" everywhere else.
 *
 * Read from the schema rather than from ProjectContext so the panel stays schema-driven, and shared
 * with the submit path so a rejected save names the field by the label the user is looking at.
 *
 * Deliberately not in ISSUANCE_FIELD_LABELS: that map is keyed by schema property and is shared with
 * the inventory grid, which must keep quantityType -> "Unit type" for Carbon and Biodiversity.
 */
export const getQuantityTypeLabel = (issuanceSchema: ProjectIssuanceSpecificSchema | undefined): string =>
  issuanceSchema?.properties?.unitMeasurement?.const ===
  ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY
    ? "Nutrient"
    : ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS.quantityType;

/**
 * What to say when the server rejects the quantity type, named by whatever the panel calls that
 * column.
 *
 * Nutrient Neutrality is the one measurement where a BLANK value arrives as "invalid" rather than
 * "required": the server exempts it from ValidateQuantityTypeRequired because null already fails the
 * schema's enum. "Is not one of the values permitted" for an untouched picker reads as though the
 * user chose something wrong, so an empty value is named as missing - the same wording Carbon gets
 * from VALIDATION_QUANTITY_TYPE_REQUIRED.
 */
export const getQuantityTypeErrorMessage = (
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined,
  submittedQuantityType: string | null | undefined
): string => {
  const label = getQuantityTypeLabel(issuanceSchema);
  return submittedQuantityType
    ? `${label} is not one of the values permitted by this standard`
    : `${label} is a required field`;
};

export const shouldRenderSchemaField = (fieldKey: string, definition: ProjectIssuanceSpecificProperty): boolean => {
  return !COMMON_ISSUANCE_FIELD_KEYS.has(fieldKey) && !isReadOnlyOrConstField(definition);
};

export const normaliseFieldLabel = (fieldKey: string): string => {
  if (ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS[fieldKey]) {
    return ProjectIssuanceConstants.ISSUANCE_FIELD_LABELS[fieldKey];
  }

  return camelCaseToSentenceCase(fieldKey);
};

export const getIssuanceFieldValue = (issuanceData: IssuancePanelData, fieldKey: string): unknown => {
  const standardSpecificIssuanceData = issuanceData.standardSpecificIssuanceData as unknown as Record<string, unknown>;

  if (Object.prototype.hasOwnProperty.call(standardSpecificIssuanceData, fieldKey)) {
    return standardSpecificIssuanceData[fieldKey];
  }

  return (issuanceData as unknown as Record<string, unknown>)[fieldKey];
};

export const setIssuanceFieldValue = (
  setIssuanceData: Dispatch<SetStateAction<IssuancePanelData>>,
  fieldKey: string,
  value: unknown
): void => {
  setIssuanceData((previousData) => {
    const updatedStandardSpecificIssuanceData = {
      ...(previousData.standardSpecificIssuanceData as unknown as Record<string, unknown>),
      [fieldKey]: value,
    } as unknown as StandardSpecificIssuanceData;

    return {
      ...previousData,
      standardSpecificIssuanceData: updatedStandardSpecificIssuanceData,
    } as IssuancePanelData;
  });
};

const findMatchingCondition = (
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined,
  getFieldValue: (field: string) => unknown
): ProjectIssuanceSpecificSchemaCondition | undefined => {
  if (!issuanceSchema?.allOf || issuanceSchema.allOf.length === 0) {
    return undefined;
  }

  return issuanceSchema.allOf.find((condition) => {
    const conditionProperties = condition.if?.properties;

    if (!conditionProperties) {
      return false;
    }

    return Object.entries(conditionProperties).every(([conditionFieldKey, conditionDefinition]) => {
      if (conditionDefinition.const === undefined) {
        return true;
      }

      const conditionValue = getFieldValue(conditionFieldKey);
      return conditionValue != null && String(conditionValue) === conditionDefinition.const;
    });
  });
};

export const getConditionalEnumValues = (
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined,
  fieldKey: string,
  getFieldValue: (field: string) => unknown
): string[] | undefined => {
  return findMatchingCondition(issuanceSchema, getFieldValue)?.then?.properties?.[fieldKey]?.enum;
};

/**
 * Reads a value the schema pins for the current form state — e.g. the BNG issuance schema derives
 * quantityType from habitatType as `then: { quantityType: { const: ... } }` (BR-02855/BR-02856).
 */
export const getConditionalConstValue = (
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined,
  fieldKey: string,
  getFieldValue: (field: string) => unknown
): string | undefined => {
  const constValue = findMatchingCondition(issuanceSchema, getFieldValue)?.then?.properties?.[fieldKey]?.const;

  return typeof constValue === "string" ? constValue : undefined;
};
